import React, { useState } from "react";
import styles from "./Tutorials.module.scss";
import { useNavigate } from "react-router-dom";
import { PlayCircle, HelpCircle, GraduationCap } from "lucide-react";

import { formatDate } from "@Utils/functions";
import { categories } from "@Types/lender_tutorials";
import VideoPlayer from "@Components/VideoPlayer/VideoPlayer";
import useGetTutorials, { useLikeTutorial } from "@Hooks/useLenderTutorials";

import LikeButton from "../ReleaseNotes/LikeButton";

const TutorialsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: tutorials } = useGetTutorials();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [expandedTutorial, setExpandedTutorial] = useState<string | null>(null);

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
    setExpandedTutorial(null);
  };

  const toggleTutorial = (tutorialId: string) => {
    setExpandedTutorial(expandedTutorial === tutorialId ? null : tutorialId);
  };

  const handleNavigateToFAQ = () => navigate("/faq");

  return (
    <div className={styles.tutorialPageContainer}>
      <h2 className={styles.title}>Tutorials</h2>
      <p>
        Learn how to use all features of our app with these step-by-step video
        tutorials.
      </p>

      <div className={styles.tutorialContainer}>
        {(categories ?? []).map((section) => (
          <div key={section} className={styles.section}>
            <div
              className={styles.sectionHeader}
              onClick={() => toggleSection(section)}
            >
              <h2>{section}</h2>
              <span className={styles.arrow}>
                {expandedSection === section ? "▼" : "►"}
              </span>
            </div>

            {expandedSection === section && (
              <div className={styles.tutorialsList}>
                {tutorials
                  ?.filter((x) => x.data.info.category === section)
                  .map((tutorial) => (
                    <div key={tutorial._id} className={styles.tutorialWrapper}>
                      <div
                        className={styles.tutorialItem}
                        onClick={() => toggleTutorial(tutorial._id)}
                      >
                        <div className={styles.playButton}>
                          <GraduationCap size={20} />
                        </div>
                        <div className={styles.tutorialInfo}>
                          <h3>{tutorial.data.info.title}</h3>
                        </div>
                        {tutorial.data.video?.data.info.videoUrl && (
                          <div className={styles.actionButton}>
                            <PlayCircle size={20} />
                          </div>
                        )}
                      </div>
                      {expandedTutorial === tutorial._id && (
                        <div className={styles.tutorialContent}>
                          {tutorial.data.video?.data.info.videoUrl && (
                            <div className={styles.videoPlaceholder}>
                              <div className="added-videos">
                                <h4>{`Video Description: ${
                                  tutorial.data.video?.data.info.description ??
                                  ""
                                }`}</h4>
                                <VideoPlayer
                                  publicUrl={
                                    tutorial.data.video?.data.info.videoUrl
                                  }
                                />
                              </div>
                            </div>
                          )}

                          <div className={styles.tutorialDescription}>
                            <div className={styles.tutorialInfoContainer}>
                              <h4>Description</h4>
                              <div className={styles.tutorialInformation}>
                                <div>
                                  {`Created at: ${formatDate(
                                    tutorial.createdAt,
                                    "medium"
                                  )}`}
                                </div>
                                <div>
                                  {`Last Updated: ${formatDate(
                                    tutorial.updatedAt,
                                    "medium"
                                  )}`}
                                </div>
                                <LikeButton
                                  entity={tutorial}
                                  className={styles.likeButton}
                                  useLike={useLikeTutorial}
                                />
                              </div>
                            </div>

                            <p>{tutorial.data.info.body}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        ))}

        <div className={styles.resourcesSection} onClick={handleNavigateToFAQ}>
          <div className={styles.resourceIcon}>
            <HelpCircle size={24} />
          </div>
          <span>Additional System Resources</span>
        </div>
      </div>
    </div>
  );
};

export default TutorialsPage;
